if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item']
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      parseVariantMediaIds() {
        this.variantMediaMap = {};
        this.variantSpecificMediaIds = new Set();

        const variantMediaString = this.dataset.variantMediaIds;
        const pairs = variantMediaString.split(';').filter((p) => p.trim());

        pairs.forEach((pair) => {
          const [mediaId, variantId] = pair.split(',').map((s) => s.trim());
          if (mediaId && variantId) {
            if (!this.variantMediaMap[variantId]) {
              this.variantMediaMap[variantId] = [];
            }
            if (!this.variantMediaMap[variantId].includes(mediaId)) {
              this.variantMediaMap[variantId].push(mediaId);
            }
            this.variantSpecificMediaIds.add(mediaId);
          }
        });
      }

      filterMediaByVariant(variant) {
        const variantId = variant.id.toString();
        const variantMediaIds = this.variantMediaMap[variantId] || [];

        const noVariantSpecificMedia = this.variantSpecificMediaIds.size === 0;

        const allowedMediaIds = new Set(variantMediaIds);
        if (!noVariantSpecificMedia) {
          this.querySelectorAll('.product__media-item').forEach((item) => {
            const id = item.dataset.mediaId;
            if (!this.variantSpecificMediaIds.has(id)) {
              allowedMediaIds.add(id);
            }
          });
        }

        // Filter thumbnails
        const allThumbnails = this.querySelectorAll('.product__thumbs-item');
        allThumbnails.forEach((thumbnail) => {
          const mediaId = thumbnail.dataset.mediaId;
          if (noVariantSpecificMedia || allowedMediaIds.has(mediaId)) {
            thumbnail.style.display = '';
            thumbnail.classList.remove('hidden');
            thumbnail.classList.add('swiper-slide');
          } else {
            thumbnail.style.display = 'none';
            thumbnail.classList.add('hidden');
            thumbnail.classList.remove('swiper-slide');
          }
        });

        // Filter main media items
        const allMediaItems = this.querySelectorAll('.product__media-item');
        allMediaItems.forEach((mediaItem) => {
          const mediaId = mediaItem.dataset.mediaId;
          if (noVariantSpecificMedia || allowedMediaIds.has(mediaId)) {
            mediaItem.classList.remove('hidden');
            mediaItem.classList.add('swiper-slide');
          } else {
            mediaItem.classList.add('hidden');
            mediaItem.classList.remove('swiper-slide');
          }
        });

        this.reinitSliderAfterFilter();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      reinitSliderAfterFilter() {
        // Destroy existing slider instances
        if (this.sliderInstance && typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy(true, false);
          this.sliderInstance = false;
        }
        if (this.thumbsInstance && typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy(true, false);
          this.thumbsInstance = false;
        }

        // Clean up leftover Swiper classes that could confuse new instances
        this.querySelectorAll(
          '.swiper-slide-active, .swiper-slide-thumb-active, .swiper-slide-next, .swiper-slide-prev'
        ).forEach((el) => {
          el.classList.remove(
            'swiper-slide-active',
            'swiper-slide-thumb-active',
            'swiper-slide-next',
            'swiper-slide-prev',
            'swiper-slide-visible'
          );
        });

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        if (this.elements.thumbnails) {
          this.setSliderOptions();
          this.initSlider();
        } else {
          this.updateMediaLayout();
        }
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.parseVariantMediaIds();

        if (this.initialized === true) return;

        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction'
          },
          threshold: 2
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal'
            },
            768: {
              spaceBetween: mediaItemGap
            }
          }
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap
              }
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5
              },
              1536: {
                slidesPerView: 7
              }
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }

        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.hidden)')];
        if (allMedia) {
          allMedia.forEach((media, visibleIndex) => {
            const { mediaType, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: visibleIndex,
              mediaType: mediaType
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          // Don't restore focus to the opener thumbnail on close. The opener lives
          // inside the slide it was opened from, so when the tab is re-activated the
          // browser re-focuses (and scrolls) that element into view, hijacking the
          // swiper back to the opening index. Keeping focus off it prevents the jump.
          returnFocus: false
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          return this.querySelector(`.product__media-item[data-media-index="${id}"] img`) || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          const { currIndex } = this.lightbox?.pswp;
          const slider = this.sliderInstance?.slider;
          if (slider) {
            slider.slideTo(currIndex, 0, false);
          }
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox?.pswp;
          if (!this.sliderInstance && currIndex !== null) {
            const target = this.elements.mediaItems?.find((item) => Number(item.dataset.mediaIndex) === currIndex);
            if (target) {
              requestAnimationFrame(() => {
                const rect = target.getBoundingClientRect();
                const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (!inView) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              }
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              }
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            }
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              // Find actual index among visible slides (not the static data-media-index), if this is not updated then
              // lightbox will open random images in next sequence as we are hiding the images which are not related to
              // particular variant and slider index is based on all media items not the visible media items
              const visibleSlides = [...this.querySelectorAll('.product__media-item:not(.hidden)')];
              const mediaItem = media.closest('.product__media-item');
              const index = visibleSlides.indexOf(mediaItem);
              this.lightbox.loadAndOpen(index >= 0 ? index : 0);
            }
          }
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);

            this.trackProductImage(slides[activeIndex], swiper.realIndex);
          }
        });
      }

      // GA4 product_image: user navigates to another PDP media (next/prev/thumb).
      // Skipped when the slide change is programmatic (e.g. variant switch).
      trackProductImage(slide, realIndex) {
        if (this._suppressImageTrack || !window.BritaGx || !slide) return;
        window.BritaGx.track('product_image', {
          image_file: this.mediaFileName(slide),
          image_position: String((realIndex || 0) + 1),
          image_type: slide.dataset.mediaType || 'image'
        });
      }

      mediaFileName(slide) {
        const src = slide.dataset.src || (slide.querySelector('img') && slide.querySelector('img').currentSrc);
        if (src) {
          const clean = src.split('?')[0].split('/').pop();
          if (clean) return clean;
        }
        return slide.dataset.mediaId || '';
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        this.filterMediaByVariant(variant);

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image'
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance && this.sliderInstance.slider) {
          // Find the correct index among visible slides
          const visibleSlides = [...this.querySelectorAll('.product__media-item:not(.hidden)')];
          const slideIndex = visibleSlides.findIndex((slide) => slide.dataset.mediaId == featuredMedia.id);
          if (slideIndex >= 0) {
            this._suppressImageTrack = true;
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
            requestAnimationFrame(() => {
              this._suppressImageTrack = false;
            });
          }
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image'
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
